import {
	CreateHubTemplateRequestModel,
	getCreateViewModel,
	create,
	CreateHubTemplateComposerViewModel,
	CreateHubTemplateResultModel,
	CreateHubTemplateResultKind,
	CreateHubTemplateOptionsModel
} from "client/Api/Hub/Templates/hubTemplateManagement";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUnmountedCancellation,
	useValueEqualMemo
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { prepareHubTemplateForSaving } from "./EditHubTemplateDialog";
import {
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import {
	Anchor,
	Dialog,
	Form,
	FormattedText,
	Space,
	Toolbar,
	useConfirmationDialog
} from "client/Components";
import {
	FormData,
	HubTemplateFormContent,
	HubTemplateFormContentHandle,
	hubTemplateFormValidationConfiguration
} from "./HubTemplateFormContent";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { configureValidationStep } from "client/Validation/steps";
import { CreateTemplateNameDialog } from "./CreateTemplateNameDialog";
import {
	convertLayoutToSaveModel,
	convertMediaToSaveModel,
	createCommonMediaObject,
	getOriginalImagesFromLayout
} from "../../converters";
import {
	EMPTY_ARRAY,
	isNilOrWhiteSpace,
	isNotNil
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import { never } from "client_legacy/ndaw/errors";
import { HashNavigationRouteActionKind, generateFragment } from "client/Features/Navigation/routing";
import { getHubUrlForPage } from "../../HubOverviewPage";
import { HubItemImageRestrictionsModel, HubItemMediaKind } from "client/Api/Hub/common";
import {
	BlobIdImageElementComposerViewModel,
	BlobIdImageHubMediaComposerViewModel,
	ExistingHubItemImages,
	HubImageMediaKind
} from "client/Api/Hub/common.composer";
import {
	createLayoutValidation,
	createMediaValidation
} from "../../Builder/validation";
import { IObjectValidatorBuilder } from "client/Validation/builder";
import { DocumentLocationViewModel } from "client/Api/Hub/hubSettings";

export type CreateHubTemplateDialogBasedOn =
	| {
		templateId?: number;
	}
	| {
		hubItemId?: number;
	};

export type CreateHubTemplateDialogProps = {
	kind: HubItemKind;
	basedOn?: CreateHubTemplateDialogBasedOn;
	onClose(): void;
	onSave(id: number): void;
	onBack?(): void;
};

export const CreateHubTemplateDialog = (props: CreateHubTemplateDialogProps): ReactElement => {
	const {
		kind,
		basedOn = {},
		onClose: onCloseFromProps,
		onSave,
		onBack
	} = props;

	const onClose: () => void = useStableImperativeCallback(onCloseFromProps);

	const [formData, setFormData] = useState<RequestFormModel>(() => createFormData(kind));
	const [initialFormData, setInitialFormData] = useState<RequestFormModel>(() => createFormData(kind));
	const [state, setState] = useState<State>(defaultState);
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [nameDialogState, setNameDialogState] = useState<NameDialogState>({
		isVisible: false
	});

	const shouldShowCorrectStepForValidationRef: RefObject<boolean> = useRef<boolean>(false);
	const contentRef: RefObject<HubTemplateFormContentHandle | null> = useRef<HubTemplateFormContentHandle>(null);

	const alertManager: AlertManager = useAlerts();

	const {
		isLoaded,
		imageRestrictions = {
			maxImageSize: 0,
			supportedFileExtensions: EMPTY_ARRAY,
			supportedMimeTypes: EMPTY_ARRAY
		},
		defaultImageLocation = null,
		originalImages,
		isPixabayImageGalleryAvailable = false
	} = state;

	const validationConfiguration = useMemo(
		(): IObjectValidatorBuilder<RequestFormModel> => {
			const result: IObjectValidatorBuilder<RequestFormModel> = staticValidationConfiguration.clone();

			const saveModelValidation: IObjectValidatorBuilder<FormData> = result.forObject("model");

			saveModelValidation.forObject("media")
				.addValidators(
					createMediaValidation(
						imageRestrictions.supportedMimeTypes,
						imageRestrictions.maxImageSize
					)
				);

			saveModelValidation.forObject("layout")
				.addValidators(
					createLayoutValidation(
						imageRestrictions.supportedMimeTypes,
						imageRestrictions.maxImageSize,
						true
					)
				);

			return result;
		},
		[
			imageRestrictions.supportedMimeTypes,
			imageRestrictions.maxImageSize,
		]
	);

	const formHandler: IFormHandler<RequestFormModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();

	const isDataChanged = useMemo(
		(): boolean => isNotNil(initialFormData)
			&& !deepComparison(formData, initialFormData),
		[
			formData,
			initialFormData
		]
	);

	const createHubTemplateOptions = useValueEqualMemo(
		(): CreateHubTemplateOptionsModel => {
			if ("templateId" in basedOn) {
				return { basedOnTemplateId: basedOn.templateId };
			}

			if ("hubItemId" in basedOn) {
				switch (kind) {
					case HubItemKind.Announcement:
						return { basedOnAnnouncementId: basedOn.hubItemId };
					case HubItemKind.KnowledgeItem:
						return { basedOnKnowledgeItemId: basedOn.hubItemId };
					case HubItemKind.Event:
						return { basedOnEventId: basedOn.hubItemId };
					default:
						never(kind);
				}
			}

			return { };
		},
		[basedOn, kind]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchCreateViewModel(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					const viewModel: CreateHubTemplateComposerViewModel = await getCreateViewModel(
						createHubTemplateOptions,
						signal
					);

					const formData: RequestFormModel = await convertToFormData(kind, viewModel);
					setFormData(formData);
					setInitialFormData(formData);

					const {
						imageRestrictions,
						defaultImageLocation,
						isPixabayImageGalleryAvailable
					} = viewModel;

					setState({
						isLoaded: true,
						imageRestrictions,
						defaultImageLocation,
						originalImages: getOriginalImages(viewModel),
						isPixabayImageGalleryAvailable
					});

					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			}

			const cancellation = new AbortController();

			fetchCreateViewModel(cancellation.signal);

			return (): void => cancellation.abort();
		},
		[
			alertManager,
			createHubTemplateOptions,
			kind,
			onClose
		]
	);

	useEffect(
		(): void => {
			if (!shouldShowCorrectStepForValidationRef.current) {
				return;
			}

			shouldShowCorrectStepForValidationRef.current = false;

			const isNameValid: boolean = formHandler.validation
				.getValidationStepStates(nameValidationStep)
				.every(x => x.kind === InputValidationResultKind.Valid);

			if (isNameValid) {
				setNameDialogState({
					isVisible: false
				});
			} else {
				setNameDialogState({
					isVisible: true,
					isBusy: false
				});
			}
		},
		[formHandler.validation]
	);

	const onBeforeUnload = useCallback(
		(event: BeforeUnloadEvent) => {
			const isChanged: boolean = isDataChanged
				|| (
					contentRef.current
						?.getIsContentChanged() ?? false
				);

			if (isChanged) {
				beforeUnloadListener(event);
			}
		},
		[isDataChanged]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			window
				.addEventListener(
					"beforeunload",
					onBeforeUnload
				);

			return () => window
				.removeEventListener(
					"beforeunload",
					onBeforeUnload
				);
		},
		[onBeforeUnload]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler.validation
					.validateStep(formValidationStep, globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				setNameDialogState({
					isVisible: true,
					isBusy: false
				});
				setFormData(
					formData => {
						if (isNilOrWhiteSpace(formData.name)) {
							return {
								...formData,
								name: formData.model
									.title
							};
						}

						return formData;
					}
				);
				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);

				alertManager.showError("_ChangesNotSaved");
			}
		},
		[
			alertManager,
			formHandler.validation,
			globalCancellation,
			isBusy
		]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setNameDialogState({
				isVisible: true,
				isBusy: true
			});

			try {
				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					shouldShowCorrectStepForValidationRef.current = true;
					return;
				}

				const result: CreateHubTemplateResultModel = await create(
					prepareForSaving(formData),
					globalCancellation
				);

				switch (result.kind) {
					case CreateHubTemplateResultKind.MaxNumberOfTemplatesReached:
						alertManager.showError(
							<>
								<FormattedText
									text={localize("_HubTemplatesMaxCountErrorMessage")}
									thingsToReplace={[
										{
											what: "{{MaxCount}}",
											filler: maxNumberOfHubTemplates
										}
									]}
								/>
								<Space />
								<FormattedText
									text={localize("_ClickHereToManageTemplates")}
									thingsToWrap={[
										{
											blockId: "Here",
											wrap: (children) => (
												<Anchor
													href={getTemplateManagementUrl(kind)}
													target="_blank"
												>
													{children}
												</Anchor>
											)
										}
									]}
								/>
							</>
						);
						setNameDialogState({
							isVisible: true,
							isBusy: false
						});

						return;
					case CreateHubTemplateResultKind.Success:
						alertManager.showSuccess(localize("_SuccessfullySavedAsTemplate"));
						onSave(result.id);
						return;
					default:
						never(result);
				}
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				setNameDialogState({
					isVisible: true,
					isBusy: false
				});

				if (formHandler.validation.tryProcessServerError(e)) {
					shouldShowCorrectStepForValidationRef.current = true;
					return;
				}

				alertManager.showError(localize("_ChangesNotSaved"));
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			kind,
			onSave
		]
	);

	const checkIfCanNavigateAway = useCallback(
		async (): Promise<boolean> => {
			const hasChanges: boolean = isDataChanged
				|| (
					contentRef.current
						?.getIsContentChanged() ?? false
				);

			if (!hasChanges) {
				return true;
			}

			const leaveConfirmation: TryResult<void> = await openConfirmationDialog({
				title: localize("_AreYouSureYouWantToLeaveThisPage"),
				children: localize("_YouHaveUnsavedChanges"),
				confirmText: localize("_Leave"),
				cancelText: localize("_Stay"),
				trackingId: "discard-create-hub-template-form-confirmation-dialog"
			});

			return leaveConfirmation.wasSuccessful;
		},
		[isDataChanged, openConfirmationDialog]
	);

	const backHandler = useMemo(
		(): (() => void) | undefined => {
			if (!onBack) {
				return undefined;
			}

			return async (): Promise<void> => {
				if (await checkIfCanNavigateAway()) {
					onBack();
				}
			};
		},
		[checkIfCanNavigateAway, onBack]
	);

	const closeHandler = useCallback(
		async (): Promise<void> => {
			if (await checkIfCanNavigateAway()) {
				onClose();
			}
		},
		[checkIfCanNavigateAway, onClose]
	);

	return (
		<>
			<Dialog
				variant="left-slider"
				onClose={closeHandler}
				hasBusyIndicator={isBusy}
				trackingId="create-hub-template-dialog"
			>
				<Dialog.Header
					title={localize("_NewTemplate")}
					onClose={closeHandler}
					onBack={backHandler}
					hasBorder
				/>
				<Dialog.Body goesEdgeToEdge>
					<Form
						height="fill"
						onSubmit={saveHandler}
					>
						{isLoaded && (
							<HubTemplateFormContent
								attachable={formHandler.for("model")}
								hubItemKind={kind}
								imageRestrictions={imageRestrictions}
								defaultImageLocation={defaultImageLocation}
								originalImages={originalImages}
								ref={contentRef}
								isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
							/>
						)}
					</Form>
				</Dialog.Body>
				<Dialog.Footer hasBorder>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize("_Save")}
							onClick={saveHandler}
							trackingId="save"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{nameDialogState.isVisible && (
				<CreateTemplateNameDialog
					attachable={formHandler.for("name")}
					isBusy={nameDialogState.isBusy}
					onClose={() => setNameDialogState({ isVisible: false })}
					onSave={submitHandler}
				/>
			)}
			{confirmationDialog}
		</>
	);
};
CreateHubTemplateDialog.displayName = "CreateHubTemplateDialog";

export type CreateHubTemplateDialogOpenData = Omit<CreateHubTemplateDialogProps, "onClose" | "onSave">
	& (
		| {
			closeDialogOn?: "close";
			onClose?: undefined;
		}
		| {
			closeDialogOn: "back";
			onClose(): void;
		}
	);

export function useCreateHubTemplateDialog(): [
	element: ReactElement | null,
	open: (openData: CreateHubTemplateDialogOpenData) => Promise<TryResult<number>>
] {
	return useDataInputDialog<CreateHubTemplateDialogOpenData, number>(
		(data, onClose, onSave) => {
			const {
				closeDialogOn: closeDialog,
				onBack,
				onClose: onCloseFromProps,
				...props
			} = data;

			const closeHandler = (): void => {
				switch (closeDialog) {
					case "close":
					case undefined:
						onClose();
						break;
					case "back":
						onCloseFromProps();
						break;
					default:
						never(closeDialog);
				}
			};

			const backHandler = isNotNil(onBack) || closeDialog === "back"
				? (): void => {
					if (closeDialog === "back") {
						onClose();
					}

					onBack?.();
				}
				: undefined;

			return (
				<CreateHubTemplateDialog
					{...props}
					onClose={closeHandler}
					onBack={backHandler}
					onSave={onSave}
				/>
			);
		}
	);
}

export const maxNumberOfHubTemplates = 50;

type RequestFormModel = OverrideProperties<
	CreateHubTemplateRequestModel,
	{
		model: FormData;
	}
>;

function createFormData(kind: HubItemKind): RequestFormModel {
	return {
		kind,
		name: "",
		model: {
			title: "",
			media: {
				...createCommonMediaObject(),
				mediaKind: HubItemMediaKind.None
			},
			layout: {
				rows: []
			}
		}
	};
}

async function convertToFormData(
	kind: HubItemKind,
	viewModel: CreateHubTemplateComposerViewModel
): Promise<RequestFormModel> {
	return {
		kind,
		name: "",
		model: {
			title: viewModel.title ?? "",
			media: await convertMediaToSaveModel(
				viewModel.media
			),
			layout: await convertLayoutToSaveModel(viewModel.layout)
		}
	};
}

function getOriginalImages(viewModel: CreateHubTemplateComposerViewModel): ExistingHubItemImages {
	const {
		media
	} = viewModel;

	const coverImage: BlobIdImageHubMediaComposerViewModel | null = media.mediaKind === HubItemMediaKind.Image
		&& media.imageKind === HubImageMediaKind.BlobId
			? media
			: null;

	const layoutImages: Map<number, BlobIdImageElementComposerViewModel> = isNotNil(viewModel.layout)
		? getOriginalImagesFromLayout(viewModel.layout)
		: new Map();

	return {
		coverImage,
		layoutImages
	};
}

function prepareForSaving(request: RequestFormModel): CreateHubTemplateRequestModel {
	return {
		...request,
		model: prepareHubTemplateForSaving(request.model)
	};
}

type State = {
	isLoaded: true;
	imageRestrictions: HubItemImageRestrictionsModel;
	defaultImageLocation: DocumentLocationViewModel | null;
	originalImages: ExistingHubItemImages;
	isPixabayImageGalleryAvailable: boolean;
} | {
	isLoaded: false;
	imageRestrictions?: undefined;
	defaultImageLocation?: undefined;
	originalImages?: undefined;
	isPixabayImageGalleryAvailable?: undefined;
};

type NameDialogState = {
	isVisible: false;
} | {
	isVisible: true;
	isBusy: boolean;
};

const defaultState: State = {
	isLoaded: false
};

const staticValidationConfiguration = createValidationConfiguration<RequestFormModel>();

staticValidationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

staticValidationConfiguration.forObject(
	"model",
	hubTemplateFormValidationConfiguration.clone()
);

const formValidationStep = configureValidationStep<RequestFormModel>()
	.add("model");

const nameValidationStep = configureValidationStep<RequestFormModel>()
	.add("name");

function getTemplateManagementUrl(kind: HubItemKind): string {
	const kindToUrlKind: Record<HubItemKind, "announcement" | "event" | "knowledgeItem"> = {
		[HubItemKind.Announcement]: "announcement",
		[HubItemKind.Event]: "event",
		[HubItemKind.KnowledgeItem]: "knowledgeItem"
	};

	const manageHubUrlFragment: string | null = generateFragment({
		action: HashNavigationRouteActionKind.HubManagementSpecificView,
		params: {
			hubItemKind: kindToUrlKind[kind],
			view: "template"
		},
		queryParams: {}
	});

	return getHubUrlForPage() + (
		isNotNil(manageHubUrlFragment)
			? `#${manageHubUrlFragment}`
			: ""
	);
}

function beforeUnloadListener(event: BeforeUnloadEvent): string {
	event.preventDefault();
	event.returnValue = localize("_YouHaveUnsavedChanges");
	return event.returnValue;
};